import { Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { getEmployeeById } from "../services/employeeservice";

const routeNameMap = {
  dashboard: "Dashboard",
  employee: "Employees",
  task: "Tasks",
  settings: "Settings",
  profile: "Profile",
  attendance: "Attendance",
  department: "Departments",
  designation: "Designations",
  users: "Users",
  roles: "Roles & Permissions",
  leaves: "Leaves",
  project: "Projects",
  team: "Teams",
  clients: "Clients",
  "work-calendar": "Work Calendar",
};

export default function Breadcrumbs() {
  const location = useLocation();

  // Maps a raw URL id segment (e.g. "64") -> a friendlier label (e.g. "SYR0001")
  const [resolvedLabels, setResolvedLabels] = useState({});

  const pathnames = location.pathname
    .split("/")
    .filter(Boolean);

  const isEmployeeEditRoute =
    pathnames[0] === "employee" && pathnames[1] === "edit" && !!pathnames[2];
  const employeeIdSegment = isEmployeeEditRoute ? pathnames[2] : null;

  useEffect(() => {
    // /employee/edit/:id -> look up the employee so we can show their
    // actual emp_id (e.g. SYR0001) instead of the raw database id.
    if (!employeeIdSegment) return;

    // Already resolved for this id (e.g. came back to this same route) — skip refetch
    if (resolvedLabels[employeeIdSegment]) return;

    let cancelled = false;

    getEmployeeById(employeeIdSegment)
      .then((res) => {
        if (cancelled) return;
        const emp = res?.data || res;
        if (emp?.emp_id) {
          setResolvedLabels((prev) => ({ ...prev, [employeeIdSegment]: emp.emp_id }));
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeIdSegment]);

  const getLabel = (segment) => {
    // Resolved dynamic id (e.g. "64" -> "SYR0001") takes priority
    if (resolvedLabels[segment]) return resolvedLabels[segment];

    // This IS the employee id segment but it hasn't resolved yet —
    // show a lightweight placeholder instead of the raw database id
    // so nothing flashes before swapping to SYR0001.
    if (employeeIdSegment && segment === employeeIdSegment) return "…";

    // Context-aware labels for the employee add/edit routes
    if (segment === "edit" && pathnames[0] === "employee") return "Edit Employee";
    if (segment === "add" && pathnames[0] === "employee") return "New Employee";

    return routeNameMap[segment] || segment.replace(/-/g, " ");
  };

  return (
    <div className="breadcrumbs">
      <Link to="/dashboard" className="crumb">
        Home
      </Link>

      {pathnames.map((segment, index) => {
        const routeTo = `/${pathnames
          .slice(0, index + 1)
          .join("/")}`;

        const isLast = index === pathnames.length - 1;
        const label = getLabel(segment);

        return (
          <span key={routeTo} className="crumb-wrapper">
            <span className="separator">/</span>
            {isLast ? (
              <span className="crumb active">
                {label}
              </span>
            ) : (
              <Link to={routeTo} className="crumb">
                {label}
              </Link>
            )}
          </span>
        );
      })}
    </div>
  );
}